/**
 * AstToTextConverter - Converts database AST format back to plain text
 *
 * PURPOSE:
 * - Loads saved AST from database
 * - Converts AST blocks back to plain text for editor
 * - Preserves paragraph structure
 *
 * COMPLEMENTS: WasmStateToAstAdapter (which converts text → AST for saving)
 */

import type { FormattingSpan } from '../core/types/FormattingSpan';

export interface DatabaseAST {
  blocks: Array<{
    id: string;
    type: string;
    children: Array<{
      id: string;
      text: string;
    }>;
    /** Per-block attributes for paragraph-level formatting (2026-01-19) */
    attrs?: {
      /** Line spacing multiplier (1.0, 1.15, 1.5, 2.0, etc.) - undefined = use document default */
      lineSpacing?: number;
      /** Font size in pixels - undefined = use document default */
      fontSizePx?: number;
      /** Font family - undefined = use document default */
      fontFamily?: string;
      /** Font weight (400 = normal, 700 = bold) - undefined = use document default */
      fontWeight?: number;
      /** Font style ('normal', 'italic', 'oblique') - undefined = use document default */
      fontStyle?: string;
      /** Version counter for cache invalidation */
      version?: number;
      /** Phase F (2026-01-21): Character-level formatting spans */
      formattingSpans?: FormattingSpan[];
      /** Legal metadata */
      level?: number;
      numbering?: string;
      indentationLevel?: number;
      contentHash?: string;
      tags?: string[];
      /** Paragraph boundary indices (stored for verification) */
      textStart?: number;
      textEnd?: number;
    };
  }>;
  metadata?: {
    docId?: string;
    versionId?: string;
    pageSetup?: any;
    fontSettings?: {
      fontFamily: string;
      fontSizePx: number;
      lineHeightMul: number;
      fontWeight?: number;        // Font weight (400=Regular, 700=Bold)
      fontStyle?: 'normal' | 'italic' | 'oblique'; // Font style
    };
    [key: string]: any;
  };
}

/**
 * AstToTextConverter - Converts saved AST back to plain text
 */
export class AstToTextConverter {
  /**
   * Convert AST blocks to plain text
   * 
   * @param ast - Database AST structure
   * @returns Plain text string with paragraph breaks and form feeds (page breaks)
   */
  static convertToText(ast: DatabaseAST): string {
    if (!ast || !ast.blocks || ast.blocks.length === 0) {
      console.warn('[AstToTextConverter] Empty AST provided');
      return '';
    }

    // Get manual page breaks from metadata (stored as grapheme indices)
    const manualPageBreaks = ast?.metadata?.paginationState?.manualPageBreaks || [];
    
    console.log('🔄 [AstToTextConverter] Converting AST to text:', {
      blockCount: ast.blocks.length,
      manualPageBreaks: manualPageBreaks.length
    });

    // Extract text from each block
    // 🔧 FIX: Include empty paragraphs to preserve document structure
    // Empty paragraphs (blank lines) are valid and must be preserved
    const paragraphs: string[] = [];
    
    for (const block of ast.blocks) {
      // 🔧 FIX: Don't skip blocks without children - they represent empty paragraphs
      if (!block.children || block.children.length === 0) {
        // Empty paragraph - add empty string to preserve the blank line
        paragraphs.push('');
        continue;
      }

      // Concatenate all runs in the block
      // 🔥 FIX: Use trimPreservingFormFeeds instead of .trim() to preserve page breaks
      const blockText = this.trimPreservingFormFeeds(
        block.children
          .map(child => child.text || '')
          .join('')
      );

      // 🔧 FIX: Always add the paragraph, even if empty
      // This preserves blank lines in the document
      paragraphs.push(blockText);
    }

    // 🔬 DIAGNOSTIC: Log paragraphs before joining
    console.log('🔬 [AstToTextConverter] Paragraphs to join:', {
      count: paragraphs.length,
      paragraphs: paragraphs.map((p, i) => ({
        idx: i,
        length: p.length,
        preview: p.length > 30 ? p.slice(0, 30) + '...' : p,
        isEmpty: p.length === 0
      }))
    });

    // 🔧 FIX (2026-01-19): Use \r as paragraph separator (MS Word model)
    //
    // MS Word model:
    // - \r (carriage return) = paragraph boundary (ENTER key)
    // - \n (line feed) = soft line break (Shift+ENTER, stays in same paragraph)
    // - WASM typesetter expects \r as paragraph delimiter
    // - AST blocks are joined with \r to match the stored textStart/textEnd indices
    //
    // Example:
    // - AST blocks: ["text1", "text2", "", "text4"]
    // - With \r join: "text1\rtext2\r\rtext4" (paragraph breaks between blocks)
    // - Empty blocks create blank paragraphs (double \r)

    let text = paragraphs.join('\r');

    console.log('🔬 [AstToTextConverter] Joined text:', {
      length: text.length,
      crCount: (text.match(/\r/g) || []).length,        // Paragraph breaks (\r)
      lfCount: (text.match(/\n/g) || []).length,        // Soft line breaks (\n)
      textPreview: text.length > 100 ? text.slice(0, 100) + '...' : text
    });

    // ✅ FIX: Only re-insert form feeds if they DON'T already exist in the text
    // The text from AST blocks may already contain \f characters (preserved during save)
    // If we re-insert based on manualPageBreaks, we would DOUBLE the page breaks
    const existingFormFeeds = (text.match(/\f/g) || []).length;
    
    if (manualPageBreaks.length > 0 && existingFormFeeds === 0) {
      // Only re-insert if the text doesn't already have form feeds
      console.log('📄 [AstToTextConverter] Re-inserting page breaks at positions:', manualPageBreaks);
      
      // Convert grapheme positions to UTF-16 positions for insertion
      const sortedBreaks = [...manualPageBreaks].sort((a, b) => b - a); // Descending order
      
      for (const graphemePos of sortedBreaks) {
        // Convert grapheme position to UTF-16 position
        // For now, assume 1:1 mapping (ASCII). Full grapheme support would need segmenter.
        const utf16Pos = graphemePos;
        
        if (utf16Pos >= 0 && utf16Pos <= text.length) {
          // Insert form feed at this position
          text = text.slice(0, utf16Pos) + '\f' + text.slice(utf16Pos);
          console.log(`📄 [AstToTextConverter] Inserted page break at position ${utf16Pos}`);
        }
      }
    } else if (existingFormFeeds > 0) {
      console.log('📄 [AstToTextConverter] Text already contains form feeds, skipping re-insertion:', {
        existingFormFeeds,
        manualPageBreaks: manualPageBreaks.length
      });
    }

    console.log('✅ [AstToTextConverter] Conversion complete:', {
      paragraphCount: paragraphs.length,
      textLength: text.length,
      hasFormFeeds: text.includes('\f'),
      formFeedCount: (text.match(/\f/g) || []).length
    });

    return text;
  }

  /**
   * Extract page setup from AST metadata
   *
   * @param ast - Database AST structure
   * @returns Page setup configuration or null
   */
  static extractPageSetup(ast: DatabaseAST): any | null {
    return ast?.metadata?.pageSetup || null;
  }

  /**
   * Extract font settings from AST metadata
   *
   * @param ast - Database AST structure
   * @returns Font settings or null if not present
   */
  static extractFontSettings(ast: DatabaseAST): { 
    fontFamily: string; 
    fontSizePx: number; 
    lineHeightMul: number;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic' | 'oblique';
  } | null {
    return ast?.metadata?.fontSettings || null;
  }

  /**
   * ✅ NEW: Extract pagination state from AST metadata (Phase 3.5)
   *
   * @param ast - Database AST structure
   * @returns Pagination state or null if not present
   */
  static extractPaginationState(ast: DatabaseAST): {
    enabled: boolean;
    lastViewedPage?: number;
    pageCount?: number;
    manualPageBreaks?: number[];
  } | null {
    return ast?.metadata?.paginationState || null;
  }

  /**
   * Phase 2: Extract paragraph metadata from AST blocks
   * Phase 2B: Enhanced to extract legal metadata
   * 2026-01-19: Enhanced to extract per-paragraph formatting (lineSpacing, fontSizePx, fontFamily)
   * Reconstructs paragraph boundaries and stable UUIDs for incremental layout
   *
   * @param ast - Database AST structure
   * @param fullText - Reconstructed full text (from convertToText)
   * @returns Array of paragraph metadata with stable IDs, legal metadata, and per-paragraph formatting
   */
  static extractParagraphs(
    ast: DatabaseAST,
    fullText: string
  ): Array<{
    id: string;
    type: string;
    version: number;
    textStart: number;
    textEnd: number;
    styleId?: string;
    // Per-paragraph formatting (2026-01-19, extended 2026-01-21)
    lineSpacing?: number;
    fontSizePx?: number;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: string;
    // Phase F (2026-01-21): Character-level formatting spans
    formattingSpans?: FormattingSpan[];
    // Phase 2B: Legal metadata
    level?: number;
    numbering?: string;
    indentationLevel?: number;
    contentHash?: string;
    tags?: string[];
  }> {
    if (!ast || !ast.blocks || ast.blocks.length === 0) {
      console.warn('[AstToTextConverter] Empty AST - cannot extract paragraphs');
      return [];
    }

    console.log('📄 [AstToTextConverter] Extracting paragraphs:', {
      blockCount: ast.blocks.length,
      textLength: fullText.length
    });

    const paragraphs: Array<{
      id: string;
      type: string;
      version: number;
      textStart: number;
      textEnd: number;
      styleId?: string;
      // Per-paragraph formatting (2026-01-19, extended 2026-01-21)
      lineSpacing?: number;
      fontSizePx?: number;
      fontFamily?: string;
      fontWeight?: number;
      fontStyle?: string;
      // Phase F (2026-01-21): Character-level formatting spans
      formattingSpans?: FormattingSpan[];
      // Phase 2B: Legal metadata
      level?: number;
      numbering?: string;
      indentationLevel?: number;
      contentHash?: string;
      tags?: string[];
    }> = [];

    let currentGraphemeIndex = 0;

    // Use Intl.Segmenter for grapheme-aware boundary detection
    const segmenter = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });

    const totalBlocks = ast.blocks.length;

    for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex++) {
      const block = ast.blocks[blockIndex];
      const isLastBlock = blockIndex === totalBlocks - 1;

      // Extract text for this block
      const blockText = block.children
        .map(child => child.text || '')
        .join('')
        .trim();

      // 🔧 FIX: Don't skip empty paragraphs - they represent blank lines
      // Empty paragraphs are valid and must be preserved for correct layout

      // Count graphemes in this block
      const blockGraphemes = Array.from(segmenter.segment(blockText));
      const graphemeCount = blockGraphemes.length;

      // Extract version from attrs if available (Phase 2 format)
      // 🔧 FIX (2026-01-21): Handle attrs being a JSON string OR object
      let attrs = (block as any).attrs || {};
      if (typeof attrs === 'string') {
        try {
          attrs = JSON.parse(attrs);
          console.log(`🔧 [AstToTextConverter] Parsed attrs from string for block ${block.id}`);
        } catch (e) {
          console.error(`❌ [AstToTextConverter] Failed to parse attrs string for block ${block.id}:`, e);
          attrs = {};
        }
      }
      const version = attrs.version || 1;

      // 🔧 FIX (2026-01-19): Calculate textStart/textEnd to match WASM's paragraph detection.
      //
      // WASM's detect_paragraphs() includes \r in textEnd:
      // - "Hello\rWorld" → Paragraph 0: [0, 6) includes "Hello\r", Paragraph 1: [6, 11)
      //
      // Since we store text WITHOUT \r (stripped in graphemeSlice), and join with \r:
      // - Block 0: "Hello" (5 graphemes) + \r delimiter → textEnd = textStart + 5 + 1 = 6
      // - Block 1: "World" (5 graphemes), no trailing \r → textEnd = textStart + 5 = 11
      //
      // The LAST paragraph does NOT have a trailing \r, so textEnd excludes the delimiter.
      const textStart = currentGraphemeIndex;
      const textEnd = isLastBlock
        ? currentGraphemeIndex + graphemeCount       // Last block: no trailing \r
        : currentGraphemeIndex + graphemeCount + 1;  // Other blocks: include \r in textEnd

      // Map AST block type back to paragraph type
      const paragraphType = this.mapBlockTypeToParagraphType(block.type);

      paragraphs.push({
        id: block.id, // ✅ Stable UUID from database
        type: paragraphType,
        version: version,
        textStart: textStart,
        textEnd: textEnd,
        styleId: (block as any).styleId,
        // ✅ Per-paragraph formatting (2026-01-19, extended 2026-01-21) - conditionally added
        ...(attrs.lineSpacing !== undefined && { lineSpacing: attrs.lineSpacing }),
        ...(attrs.fontSizePx !== undefined && { fontSizePx: attrs.fontSizePx }),
        ...(attrs.fontFamily !== undefined && { fontFamily: attrs.fontFamily }),
        ...(attrs.fontWeight !== undefined && { fontWeight: attrs.fontWeight }),
        ...(attrs.fontStyle !== undefined && { fontStyle: attrs.fontStyle }),
        // ✅ Phase F (2026-01-21): Character-level formatting spans
        ...(attrs.formattingSpans && attrs.formattingSpans.length > 0 && { formattingSpans: attrs.formattingSpans }),
        // ✅ Phase 2B: Legal metadata (conditionally added)
        ...(attrs.level !== undefined && { level: attrs.level }),
        ...(attrs.numbering && { numbering: attrs.numbering }),
        ...(attrs.indentationLevel !== undefined && { indentationLevel: attrs.indentationLevel }),
        ...(attrs.contentHash && { contentHash: attrs.contentHash }),
        ...(attrs.tags && { tags: attrs.tags })
      });

      // 🔬 DIAGNOSTIC: Log each paragraph's boundaries AND formatting
      console.log(`📄 [AstToTextConverter] Block ${block.id}:`, {
        storedStart: attrs.textStart,
        storedEnd: attrs.textEnd,
        calculatedStart: textStart,
        calculatedEnd: textEnd,
        text: blockText.length > 30 ? blockText.slice(0, 30) + '...' : blockText,
        graphemeCount,
        isLastBlock,
        // 🆕 2026-01-20: Log per-paragraph formatting being extracted
        lineSpacing: attrs.lineSpacing,
        fontSizePx: attrs.fontSizePx,
        fontFamily: attrs.fontFamily,
        // 🆕 Phase F (2026-01-21): Log formattingSpans extraction
        hasFormattingSpans: !!(attrs.formattingSpans && attrs.formattingSpans.length > 0),
        formattingSpansCount: attrs.formattingSpans?.length || 0,
        formattingSpans: attrs.formattingSpans
      });

      // Advance to next paragraph start position
      // All blocks advance by graphemeCount + 1 (the content plus the \r delimiter)
      // Even the last block advances by +1 to keep currentGraphemeIndex consistent,
      // but textEnd is calculated WITHOUT +1 for the last block.
      currentGraphemeIndex += graphemeCount + 1;
    }

    console.log('✅ [AstToTextConverter] Extracted paragraphs:', {
      paragraphCount: paragraphs.length,
      totalGraphemes: currentGraphemeIndex
    });

    return paragraphs;
  }

  /**
   * Map AST block type back to paragraph type
   * Phase 2: Inverse of WasmStateToAstAdapter.mapParagraphTypeToBlockType
   *
   * @param blockType - AST block type ("Paragraph", "Heading", etc.)
   * @returns Paragraph type ("text", "heading", etc.)
   */
  private static mapBlockTypeToParagraphType(blockType: string): string {
    switch (blockType) {
      case 'Heading': return 'heading';
      case 'List': return 'list';
      case 'ListItem': return 'list';
      case 'Section': return 'quote'; // Ambiguous: could be quote or code
      case 'Clause': return 'text';
      case 'Paragraph':
      default:
        return 'text';
    }
  }

  /**
   * Validate AST structure
   * 
   * @param ast - Database AST structure
   * @returns true if valid, false otherwise
   */
  static isValidAST(ast: any): ast is DatabaseAST {
    return (
      ast &&
      typeof ast === 'object' &&
      Array.isArray(ast.blocks) &&
      ast.blocks.every(
        (block: any) =>
          block.id &&
          block.type &&
          Array.isArray(block.children) &&
          block.children.every((child: any) => child.id && typeof child.text === 'string')
      )
    );
  }

  /**
   * 🔥 FIX: Trim whitespace while preserving form feeds (\f)
   * 
   * Standard .trim() treats form feed as whitespace and removes it.
   * This method preserves form feeds (page breaks) while trimming other whitespace.
   * 
   * @param text - Text to trim
   * @returns Trimmed text with form feeds preserved
   */
  private static trimPreservingFormFeeds(text: string): string {
    if (!text) return '';
    
    const FORM_FEED_PLACEHOLDER = '___FORM_FEED_PLACEHOLDER___';
    
    // Replace form feeds with a placeholder before trimming
    const textWithPlaceholders = text.replace(/\f/g, FORM_FEED_PLACEHOLDER);
    
    // Trim regular whitespace
    let trimmedText = textWithPlaceholders.trim();
    
    // Restore form feeds
    trimmedText = trimmedText.replace(new RegExp(FORM_FEED_PLACEHOLDER, 'g'), '\f');
    
    return trimmedText;
  }
}

